import { useEffect, useRef, useState } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { LocateFixed, Check, Loader2, Search } from 'lucide-react';
import { toast } from 'sonner';

// Client-side coordinate extraction from an existing pasted link (Google
// Maps formats or raw "lat, lng"). what3words can't be parsed client-side.
const COORD_RES = [
  /!3d(-?\d{1,3}\.\d+)!4d(-?\d{1,3}\.\d+)/,
  /[?&]q=(-?\d{1,3}\.\d+)\s*,\s*(-?\d{1,3}\.\d+)/,
  /@(-?\d{1,3}\.\d+),(-?\d{1,3}\.\d+)/,
  /^\s*(-?\d{1,3}\.\d+)\s*,\s*(-?\d{1,3}\.\d+)\s*$/,
];
const parseCoords = (link) => {
  for (const re of COORD_RES) {
    const m = (link || '').match(re);
    if (m) return [parseFloat(m[1]), parseFloat(m[2])];
  }
  return null;
};

const PIN_ICON = L.divIcon({
  className: '',
  html: `<svg width="32" height="44" viewBox="0 0 32 44" xmlns="http://www.w3.org/2000/svg">
    <path d="M16 0C7.2 0 0 7.2 0 16c0 12 16 28 16 28s16-16 16-28C32 7.2 24.8 0 16 0z" fill="#eab308" stroke="#111" stroke-width="1.5"/>
    <circle cx="16" cy="16" r="6" fill="#111"/>
  </svg>`,
  iconSize: [32, 44],
  iconAnchor: [16, 44],
});

// Interactive map (OpenStreetMap, no API key) for picking a clock-in point.
// Confirming returns a Google-Maps-style link the backend geofence parser
// already understands: https://maps.google.com/?q=<lat>,<lng>
export default function MapPickerDialog({ open, onOpenChange, initialLink, postcode, siteName, onPick }) {
  const mapDivRef = useRef(null);
  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const [pos, setPos] = useState(null);
  const [locating, setLocating] = useState(false);
  const [searchQ, setSearchQ] = useState('');
  const [searching, setSearching] = useState(false);

  const setPoint = (lat, lng, zoom = null) => {
    setPos([lat, lng]);
    if (!mapRef.current) return;
    if (markerRef.current) {
      markerRef.current.setLatLng([lat, lng]);
    } else {
      markerRef.current = L.marker([lat, lng], { icon: PIN_ICON, draggable: true })
        .addTo(mapRef.current)
        .on('dragend', (e) => {
          const p = e.target.getLatLng();
          setPos([p.lat, p.lng]);
        });
    }
    if (zoom) mapRef.current.setView([lat, lng], zoom);
  };

  const geocode = async (q) => {
    const r = await fetch(`https://nominatim.openstreetmap.org/search?format=json&limit=1&q=${encodeURIComponent(q)}`);
    const d = await r.json();
    if (d && d[0]) return [parseFloat(d[0].lat), parseFloat(d[0].lon)];
    return null;
  };

  useEffect(() => {
    if (!open) return;
    const t = setTimeout(async () => {
      if (!mapDivRef.current || mapRef.current) return;
      const map = L.map(mapDivRef.current, { zoomControl: true }).setView([54.5, -2.5], 5);
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        maxZoom: 19,
        attribution: '&copy; OpenStreetMap contributors',
      }).addTo(map);
      map.on('click', (e) => setPoint(e.latlng.lat, e.latlng.lng));
      mapRef.current = map;
      setTimeout(() => map.invalidateSize(), 150);

      const existing = parseCoords(initialLink);
      if (existing) {
        setPoint(existing[0], existing[1], 17);
      } else if ((postcode || '').trim() || (siteName || '').trim()) {
        try {
          const c = (postcode || '').trim()
            ? await geocode(`${postcode.trim()}, UK`)
            : await geocode(`${siteName.trim()}, UK`);
          if (c && mapRef.current) mapRef.current.setView(c, 15);
        } catch { /* keep UK view */ }
      }
    }, 200);
    return () => {
      clearTimeout(t);
      if (mapRef.current) { mapRef.current.remove(); mapRef.current = null; }
      markerRef.current = null;
      setPos(null);
      setSearchQ('');
    };
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const useMyLocation = () => {
    if (!navigator.geolocation) { toast.error('GPS is not available on this device/browser'); return; }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (p) => { setLocating(false); setPoint(p.coords.latitude, p.coords.longitude, 18); },
      () => { setLocating(false); toast.error('Could not get your location — check GPS/location permission'); },
      { enableHighAccuracy: true, timeout: 12000, maximumAge: 0 }
    );
  };

  const runSearch = async () => {
    if (!searchQ.trim()) return;
    setSearching(true);
    try {
      const c = await geocode(`${searchQ.trim()}, UK`);
      if (c) { mapRef.current?.setView(c, 16); }
      else toast.error('No results — try a postcode or a fuller address');
    } catch { toast.error('Search failed — try again'); }
    setSearching(false);
  };

  const confirm = () => {
    if (!pos) { toast.error('Tap the map (or use "My location") to drop the pin first'); return; }
    onPick(`https://maps.google.com/?q=${pos[0].toFixed(6)},${pos[1].toFixed(6)}`);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card border-border rounded-sm max-w-2xl p-4" data-testid="map-picker-dialog">
        <DialogHeader>
          <DialogTitle className="font-barlow uppercase tracking-wider text-base">Pick clock-in location</DialogTitle>
        </DialogHeader>
        <div className="flex gap-2">
          <Input
            value={searchQ}
            onChange={(e) => setSearchQ(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); runSearch(); } }}
            placeholder="Search postcode / address…"
            className="bg-background border-border rounded-sm h-9"
            data-testid="map-search-input"
          />
          <Button type="button" variant="outline" onClick={runSearch} disabled={searching} className="h-9 rounded-sm shrink-0" data-testid="map-search-btn">
            {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
          </Button>
          <Button type="button" onClick={useMyLocation} disabled={locating} className="h-9 rounded-sm shrink-0 bg-emerald-600 hover:bg-emerald-500 text-white" data-testid="map-my-location-btn">
            {locating ? <Loader2 className="w-4 h-4 animate-spin sm:mr-1.5" /> : <LocateFixed className="w-4 h-4 sm:mr-1.5" />}
            <span className="hidden sm:inline">My location</span>
          </Button>
        </div>
        <div ref={mapDivRef} className="w-full h-[50vh] rounded-sm border border-border z-0" data-testid="map-canvas" />
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <p className="text-xs text-muted-foreground" data-testid="map-picked-coords">
            {pos ? `Pin: ${pos[0].toFixed(6)}, ${pos[1].toFixed(6)}` : 'Tap the map or use "My location" to drop the pin.'}
          </p>
          <Button type="button" onClick={confirm} disabled={!pos} className="rounded-sm bg-primary text-primary-foreground" data-testid="map-confirm-btn">
            <Check className="w-4 h-4 mr-1.5" /> Use this location
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
